//—— App 內建瀏覽器（in-app WebView）偵測 ——
//  用途：LINE / Threads / IG / FB 的內建瀏覽器會擋掉 sessionStorage，
//        導致 Firebase Google 登入跳去 __/auth/handler 後回來 state 遺失
//        （畫面出現 "Unable to process request due to missing initial state"）。
//        偵測到就引導用戶改用系統瀏覽器（Safari / Chrome）開啟。
//
//  參考：Google OAuth 本身也會擋部分 WebView（disallowed_useragent），
//        所以這是環境限制、非程式 bug，唯一穩解就是「外開系統瀏覽器」。

//UA 特徵 → App 代號（順序有意義：LINE 要先於 FB 家族比對）
//  line      : LINE 內建瀏覽器（有官方外開參數 openExternalBrowser=1）
//  threads   : Threads（Barcelona 是官方內部代號）
//  instagram : Instagram
//  messenger : FB Messenger
//  facebook  : Facebook App（FBAN / FBAV / FB_IAB）
const detectApp = (ua) => {
  if (/\bLine\//i.test(ua)) return "line";
  if (/Barcelona/i.test(ua)) return "threads";
  if (/Instagram/i.test(ua)) return "instagram";
  if (/Messenger/i.test(ua)) return "messenger";
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return "facebook";
  return "";
};

//App 代號 → 顯示名稱（提示卡文案用）
const APP_LABEL = {
  line: "LINE",
  threads: "Threads",
  instagram: "Instagram",
  messenger: "Messenger",
  facebook: "Facebook",
};

//偵測目前是否在 in-app 瀏覽器
//  回傳 { isInApp, app, appLabel }
export const detectInAppBrowser = () => {
  const ua =
    (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const app = detectApp(ua);
  return {
    isInApp: !!app,
    app,
    appLabel: APP_LABEL[app] || "",
  };
};

//LINE 專用：在網址加上 openExternalBrowser=1，LINE 會自動用系統瀏覽器開
//  其他 App 沒有這種官方參數，只能請用戶手動「用瀏覽器開啟」
export const buildLineExternalURL = (url = window.location.href) => {
  const [base, hash] = url.split("#");
  const sep = base.includes("?") ? "&" : "?";
  const withParam = base.includes("openExternalBrowser=")
    ? base
    : `${base}${sep}openExternalBrowser=1`;
  return hash ? `${withParam}#${hash}` : withParam;
};

//嘗試把目前頁面外開到系統瀏覽器
//  - LINE：直接導去帶 openExternalBrowser=1 的網址（成功）→ 回 true
//  - 其他：無法自動外開 → 回 false（呼叫端顯示手動教學）
export const openInExternalBrowser = (app) => {
  if (app === "line") {
    window.location.href = buildLineExternalURL();
    return true;
  }
  return false;
};

//複製目前網址到剪貼簿（給無法自動外開的 App 用）
//  回傳 Promise<boolean>
export const copyCurrentURL = async () => {
  const url = window.location.href;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch (e) {
    //clipboard API 在部分 WebView 會被擋，往下走 fallback
  }
  //fallback：用隱藏 textarea + execCommand
  try {
    const ta = document.createElement("textarea");
    ta.value = url;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
};
